import { Button } from '@vk-cloud-solutions/marketing-ui';
import axios from 'axios';
import classNames from 'classnames';

import { FormCheckbox } from '~components/Forms/FormCheckbox';
import { FormField } from '~components/Forms/FormField';
import { FormInput } from '~components/Forms/FormInput';
import { FormText } from '~components/Forms/FormText';
import { PreloaderOverlay } from '~components/PreloaderOverlay/PreloaderOverlay';
import { env } from '~constants/env';
import {
  buildFormFields,
  FieldKey,
  getRequiredMark,
} from '~helpers/forms/buildFormFields';
import { SalesUtils } from '~helpers/forms/SalesUtils';
import { prepareFosData } from '~helpers/fos/prepareFosData';
import { getUtmFromUrl } from '~helpers/getUtmFromUrl';
import { StringUtils } from '~helpers/StringUtils';
import { useForm } from '~hooks/useForm';
import { LocationUTM } from '~types/Location.types';
import { URLS } from '~types/Urls.types';

import styles from './FormRequest.module.scss';
import { FormRequestProps } from './FormRequest.types';

const fosBatut = env.blogConfig?.fosBatut;

const informationText =
  'Я даю согласие ООО «ВК Цифровые Технологии» на направление мне рекламы на условиях <a target="_blank" href="https://finops-cms-prod.s3.prodcloud.vk.team/Soglasie_na_poluchenie_reklamy_finops_4bdaa96a98.pdf">Согласия на получение рекламы</a>';

export function FormRequest({
  formLabel,
  onSuccess = () => undefined,
}: FormRequestProps) {
  const mark = (field: FieldKey) => getRequiredMark(fosBatut, field);
  const fields = buildFormFields(fosBatut);
  const {
    handleSubmit,
    handleChange,
    status,
    setStatus,
    isSubmitting,
    errors,
    values,
  } = useForm({
    onSuccess,
    fields,
    validate(fieldsValues) {
      const comment = SalesUtils.getComment({
        formLabel,
        comment: fieldsValues.comment,
      });

      if (StringUtils.size(comment) > 4096) {
        return {
          comment: 'Текст сообщения слишком длинный',
        };
      }
    },
    submit: async (data) => {
      const comment = SalesUtils.getComment({
        formLabel,
        comment: data.comment,
      });
      const utm = getUtmFromUrl({
        values: [
          LocationUTM.UtmSource,
          LocationUTM.UtmMedium,
          LocationUTM.UtmCampaign,
          LocationUTM.Gclid,
        ],
      });
      const requestBody = {
        metrics: utm,
        info: {
          comment,
          name: data.name,
          email: data.email,
          company: data.company,
          phone: data.phone.replace(/\D/gm, ''),
        },
      };

      try {
        await axios.post(
          fosBatut?.request?.url || URLS.FosBatut,
          prepareFosData({
            bitrix: {
              sourceId: 1,
              descriptionId: 933,
              comment: `comment=[${requestBody.info.comment}]`,
            },
            fields: {
              ...requestBody.info,
              information: data.information,
            },
            title: fosBatut?.data?.title || '',
          }),
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        );
        SalesUtils.sendGoals();

        setStatus({
          isSubmitting: false,
          success: {
            message: 'Спасибо за заявку, мы с Вами свяжемся в ближайшее время.',
          },
        });
      } catch (error) {
        setStatus({
          isSubmitting: false,
          error: {
            message: 'Произошла ошибка. Мы уже работаем над этим.',
          },
        });
      }
    },
  });

  return (
    <>
      {status.error || status.success ? (
        <div className={styles.formMessage}>
          <div
            className={classNames({
              [styles.successMessage]: status.success,
              [styles.errorMessage]: status.error,
            })}
          >
            {status.error?.message || status.success?.message}
          </div>
          <Button
            mod="primary"
            onClick={() => setStatus({})}
            className={styles.successButton}
          >
            Закрыть
          </Button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className={styles.form}>
          <div className={styles.fields}>
            <FormField errors={errors.phone} label={`Телефон${mark('phone')}`}>
              <FormInput
                placeholder={'+7'}
                name="phone"
                type="tel"
                value={values.phone}
                isError={!!errors.phone}
                onChange={handleChange}
                mask="+7 (999) 999-99-99"
              />
            </FormField>
            <FormField
              errors={errors.email}
              label={`Электронная почта${mark('email')}`}
            >
              <FormInput
                name="email"
                type="email"
                value={values.email}
                isError={!!errors.email}
                onChange={handleChange}
              />
            </FormField>
            <FormField errors={errors.name} label={`Имя${mark('name')}`}>
              <FormInput
                name="name"
                value={values.name}
                isError={!!errors.name}
                onChange={handleChange}
              />
            </FormField>
            <FormField
              errors={errors.company}
              label={`Компания${mark('company')}`}
            >
              <FormInput
                name="company"
                value={values.company}
                isError={!!errors.company}
                onChange={handleChange}
              />
            </FormField>
            <FormField
              errors={errors.comment}
              label={`Ваш вопрос${mark('comment')}`}
            >
              <FormText
                name="comment"
                value={values.comment}
                isError={!!errors.comment}
                onChange={handleChange}
              />
            </FormField>
          </div>
          <div className={styles.formFooter}>
            <div>
              <FormField errors={errors.privacy} className={styles.checkbox}>
                <FormCheckbox
                  name="privacy"
                  value={values.privacy}
                  checked={!!values.privacy}
                  isError={!!errors.privacy}
                  onChange={handleChange}
                />
              </FormField>
              {fields.information && (
                <FormField className={styles.informationCheckbox}>
                  <FormCheckbox
                    name="information"
                    text={informationText}
                    value={values.information}
                    checked={!!values.information}
                    onChange={handleChange}
                  />
                </FormField>
              )}
            </div>
            <Button
              mod="primary"
              type="submit"
              text={'Оставить заявку'}
              className={styles.button}
            />
          </div>
        </form>
      )}
      {isSubmitting && <PreloaderOverlay active={isSubmitting} />}
    </>
  );
}
